import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle, labelStyle } from '../components/adminTheme';
import { useLang } from '../useLang';

// Carte bordée + ombre discrète — remplace le "Windows Vista" (coins très
// arrondis + ombre lourde) par un accent léger, cohérent avec le reste du
// site (coins carrés partout, sauf l'avatar qui peut être rond).
const subtleCard = { border: `1px solid ${theme.border}`, background: theme.card, boxShadow: '0 1px 3px rgba(14,16,19,0.04)' };

function Dashboard() {
    const navigate = useNavigate();
    const [albums, setAlbums] = useState([]);
    const [stats, setStats] = useState({ albums: 0, regions: 0 });
    const [deleteModal, setDeleteModal] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [postCount, setPostCount] = useState(0);
    const { t } = useLang();

    // postcard quota
    const [postcardLimit, setPostcardLimit] = useState(5);
    const [postcardUsed, setPostcardUsed] = useState(0);
    const [showPostcardRequestModal, setShowPostcardRequestModal] = useState(false);
    const [requestReason, setRequestReason] = useState('');
    const [requestedLimit, setRequestedLimit] = useState(10);
    const [requestStatus, setRequestStatus] = useState('');

    const handleArchive = async (album) => {
        const newArchived = !album.archived;
        await supabase.from('albums').update({ archived: newArchived }).eq('title', album.title);
        setAlbums(albums.map(a => a.title === album.title ? { ...a, archived: newArchived } : a));
    };

    useEffect(() => { document.title = 'Dashboard — winston.ie Studio'; }, []);

    useEffect(() => {
        const checkUser = async () => {
            const params = new URLSearchParams(window.location.search);
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');
            if (accessToken && refreshToken) {
                await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
                window.history.replaceState({}, '', '/admin/dashboard');
            }
            if (window.location.hash) {
                const hashParams = new URLSearchParams(window.location.hash.substring(1));
                const hashAccess = hashParams.get('access_token');
                const hashRefresh = hashParams.get('refresh_token');
                if (hashAccess && hashRefresh) {
                    await supabase.auth.setSession({ access_token: hashAccess, refresh_token: hashRefresh });
                    window.history.replaceState({}, '', '/admin/dashboard');
                }
            }
            const { data } = await supabase.auth.getSession();
            if (!data.session) navigate('/admin');
        };
        checkUser();
    }, [navigate]);

    useEffect(() => {
        const fetchUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) return;
            const { data: userData } = await supabase
                .from('users')
                .select('username, role, avatar_url, postcard_daily_limit, postcard_daily_sends, postcard_last_send_date')
                .eq('email', data.session.user.email)
                .single();
            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAvatarUrl(userData.avatar_url || '');

                const todayStr = new Date().toISOString().slice(0, 10);
                const limit = userData.postcard_daily_limit || 5;
                const usedToday = userData.postcard_last_send_date === todayStr ? (userData.postcard_daily_sends || 0) : 0;
                setPostcardLimit(limit);
                setPostcardUsed(usedToday);

                let postQuery = supabase.from('posts').select('slug', { count: 'exact', head: true });
                if (userData.role !== 'ADMIN') {
                    postQuery = postQuery.or(`author_username.eq.${userData.username},co_authors.cs.{${userData.username}}`);
                }
                const { count } = await postQuery;
                setPostCount(count || 0);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        const fetchAlbums = async () => {
            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) return;
            const { data: userData } = await supabase.from('users').select('username, role, first_name, last_name').eq('email', sessionData.session.user.email).single();
            if (!userData) return;

            let query = supabase.from('albums').select('*');
            if (userData.role !== 'ADMIN') {
                const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                query = query.eq('created_by', fullName);
            }
            const { data } = await query;
            if (data) {
                setAlbums(data);
                const regions = [...new Set(data.map(a => a.region))].length;
                setStats({ albums: data.length, regions });
            }
        };
        fetchAlbums();
    }, []);

    const handleLogout = async () => { await supabase.auth.signOut(); navigate('/'); };

    const handleDelete = async () => {
        const { data } = await supabase.auth.getSession();
        const email = data.session?.user?.email;
        const { error } = await supabase.auth.signInWithPassword({ email, password: deletePassword });
        if (error) { setDeleteError('Wrong password.'); return; }
        await supabase.from('albums').delete().eq('title', deleteModal);
        setAlbums(albums.filter(a => a.title !== deleteModal));
        setDeleteModal(null); setDeletePassword(''); setDeleteError('');
    };

    const handleSubmitPostcardRequest = async () => {
        setRequestStatus('sending');
        const { error } = await supabase.from('postcard_limit_requests').insert({
            requester_username: username,
            requested_limit: requestedLimit,
            reason: requestReason,
        });
        setRequestStatus(error ? 'error' : 'sent');
    };

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .cdash-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    max-width: 1040px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cdash-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .cdash-album-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 22px;
                }
                .cdash-album-card {
                    border: 1px solid ${theme.border};
                    background: ${theme.card};
                    box-shadow: 0 1px 3px rgba(14,16,19,0.04);
                    cursor: pointer;
                    transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
                }
                .cdash-album-card:hover { border-color: ${theme.borderHover}; box-shadow: 0 8px 20px rgba(14,16,19,0.08); transform: translateY(-2px); }
                .cdash-album-card[data-archived="true"] { opacity: 0.5; }
                .cdash-album-cover { width: 100%; aspect-ratio: 1; overflow: hidden; background: #ece8df; }
                .cdash-album-cover img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .cdash-icon-btn {
                    background: none; border: none; padding: 4px; margin: -4px;
                    cursor: pointer; font-size: 13px; line-height: 1; opacity: 0.55;
                    transition: opacity .15s ease;
                }
                .cdash-icon-btn:hover { opacity: 1; }
                .cdash-album-new {
                    border: 1px dashed ${theme.border};
                    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
                    min-height: 220px; cursor: pointer; color: ${theme.textMuted};
                    font-family: ${font}; transition: border-color .2s ease, color .2s ease;
                }
                .cdash-album-new:hover { border-color: ${theme.borderHover}; color: ${theme.text}; }
            `}</style>

            <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={handleLogout} activePath="/admin/dashboard" />

            <div className="cdash-content">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', marginBottom: '8px' }}>
                    <div>
                        <p style={{ fontFamily: font, fontWeight: 700, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: theme.textMuted, margin: '0 0 8px' }}>
                            {t('yourSpace')}
                        </p>
                        <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(22px, 3vw, 30px)', letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>
                            {t('welcome')}, @{username}
                        </h1>
                    </div>
                    <div
                        onClick={() => navigate('/admin/settings')}
                        style={{
                            width: '56px', height: '56px', flexShrink: 0, overflow: 'hidden',
                            borderRadius: '50%', boxShadow: '0 3px 10px rgba(14,16,19,0.14)',
                            background: theme.border, color: theme.text, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: font, fontWeight: 700, fontSize: '14px',
                        }}
                    >
                        {avatarUrl ? (
                            <img src={avatarUrl} alt={username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            username?.slice(0, 2).toUpperCase()
                        )}
                    </div>
                </div>
                <p style={{ fontFamily: font, fontWeight: 500, fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
                    {t('dashboardSubtitle')}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', maxWidth: '480px', marginBottom: '44px' }}>
                    <div style={{ ...subtleCard, padding: '18px 16px' }}>
                        <div style={{ fontFamily: font, fontWeight: 700, fontSize: '26px', color: theme.text }}>{stats.albums}</div>
                        <div style={{ fontFamily: font, fontWeight: 500, fontSize: '11px', color: theme.textMuted, marginTop: '4px' }}>{t('albums')}</div>
                    </div>
                    <div style={{ ...subtleCard, padding: '18px 16px' }}>
                        <div style={{ fontFamily: font, fontWeight: 700, fontSize: '26px', color: theme.text }}>{stats.regions}</div>
                        <div style={{ fontFamily: font, fontWeight: 500, fontSize: '11px', color: theme.textMuted, marginTop: '4px' }}>{t('regions')}</div>
                    </div>
                    <div style={{ ...subtleCard, padding: '18px 16px', cursor: 'pointer' }} onClick={() => navigate('/admin/posts')}>
                        <div style={{ fontFamily: font, fontWeight: 700, fontSize: '26px', color: theme.text }}>{postCount}</div>
                        <div style={{ fontFamily: font, fontWeight: 500, fontSize: '11px', color: theme.textMuted, marginTop: '4px' }}>{t('posts')}</div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <span style={{ fontFamily: font, fontWeight: 700, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: theme.textMuted }}>
                        {t('albums')}
                    </span>
                    <button className="wlink" style={pillButton} onClick={() => navigate('/admin/upload')}>
                        + {t('newAlbum')}
                    </button>
                </div>

                {albums.length === 0 && (
                    <p style={{ fontFamily: font, fontWeight: 500, fontSize: '13px', color: theme.textMuted, marginBottom: '14px' }}>
                        {t('emptyAlbums')}
                    </p>
                )}

                <div className="cdash-album-grid">
                    {albums.map((album) => (
                        <div
                            key={album.title}
                            className="cdash-album-card"
                            data-archived={album.archived}
                            onClick={() => navigate(`/admin/album/${album.folder}`)}
                        >
                            <div className="cdash-album-cover">
                                {album.cover_url
                                    ? <img src={album.cover_url} alt={album.title} />
                                    : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textFaint, fontSize: '18px' }}>📷</div>
                                }
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', padding: '12px 14px 14px' }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontFamily: font, fontWeight: 700, fontSize: '13.5px', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {album.title}
                                    </div>
                                    <div style={{ fontFamily: font, fontWeight: 500, fontSize: '11px', color: theme.textMuted, marginTop: '5px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {album.region}
                                        {album.status === 'pending_review' && <span style={{ color: '#b8860b', fontWeight: 700 }}>● {t('pendingReview')}</span>}
                                        {album.status === 'rejected' && <span style={{ color: theme.danger, fontWeight: 700 }}>● {t('rejected')}</span>}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    <button className="cdash-icon-btn" onClick={(e) => { e.stopPropagation(); handleArchive(album); }}>
                                        {album.archived ? '📦' : '👁'}
                                    </button>
                                    <button className="cdash-icon-btn" onClick={(e) => { e.stopPropagation(); setDeleteModal(album.title); }}>🗑</button>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="cdash-album-new" onClick={() => navigate('/admin/upload')}>
                        <span style={{ fontSize: '20px' }}>+</span>
                        <span style={{ fontSize: '10px', fontWeight: 500 }}>{t('newAlbum')}</span>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '36px' }}>
                    <div style={{ ...subtleCard, maxWidth: '300px', padding: '14px 16px' }}>
                        <p style={{ fontFamily: font, fontWeight: 700, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: theme.textMuted, margin: '0 0 6px' }}>
                            Cartes postales
                        </p>
                        <p style={{ fontFamily: font, fontSize: '12px', color: theme.text, margin: '0 0 10px', fontWeight: 500 }}>
                            {postcardUsed}/{postcardLimit} envois aujourd'hui
                        </p>
                        {postcardUsed >= postcardLimit && (
                            <button className="wlink" style={outlineButton} onClick={() => setShowPostcardRequestModal(true)}>
                                Demander plus d'envois
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {deleteModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 }}>
                    <div style={{ background: theme.card, padding: '32px', width: 'min(320px, calc(100vw - 48px))', boxSizing: 'border-box' }}>
                        <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '8px', fontSize: '15px', color: theme.text }}>Delete album</h3>
                        <p style={{ fontFamily: font, fontSize: '13px', color: theme.textMuted, marginBottom: '20px', fontWeight: 500 }}>Enter your password to delete <strong>"{deleteModal}"</strong></p>
                        <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Password" style={{ ...inputStyle, marginBottom: '8px' }} />
                        {deleteError && <p style={{ color: theme.danger, fontFamily: font, fontSize: '12px', marginBottom: '8px', fontWeight: 500 }}>{deleteError}</p>}
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button className="wlink" style={outlineButton} onClick={() => { setDeleteModal(null); setDeletePassword(''); setDeleteError(''); }}>Cancel</button>
                            <button className="wlink" style={pillButton} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {showPostcardRequestModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 }}>
                    <div style={{ background: theme.card, padding: '28px', width: 'min(340px, calc(100vw - 48px))', boxSizing: 'border-box' }}>
                        <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '14px', fontSize: '15px', color: theme.text }}>Demander plus d'envois</h3>

                        {requestStatus === 'sent' ? (
                            <>
                                <p style={{ fontFamily: font, fontSize: '13px', color: theme.textMuted, marginBottom: '18px', fontWeight: 500 }}>Demande envoyée. L'admin va la réviser.</p>
                                <button className="wlink" style={outlineButton} onClick={() => { setShowPostcardRequestModal(false); setRequestStatus(''); }}>
                                    Fermer
                                </button>
                            </>
                        ) : (
                            <>
                                <label style={{ ...labelStyle, display: 'block' }}>Limite quotidienne souhaitée</label>
                                <input
                                    type="number"
                                    min={postcardLimit + 1}
                                    value={requestedLimit}
                                    onChange={e => setRequestedLimit(Number(e.target.value))}
                                    style={{ ...inputStyle, marginBottom: '12px' }}
                                />
                                <label style={{ ...labelStyle, display: 'block' }}>Raison (optionnel)</label>
                                <textarea
                                    value={requestReason}
                                    onChange={e => setRequestReason(e.target.value)}
                                    rows={3}
                                    style={{ ...inputStyle, marginBottom: '16px', resize: 'vertical' }}
                                />
                                <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
                                    <button className="wlink" style={outlineButton} onClick={() => setShowPostcardRequestModal(false)}>
                                        Annuler
                                    </button>
                                    <button
                                        className="wlink"
                                        style={{ ...pillButton, opacity: requestStatus === 'sending' ? 0.5 : 1 }}
                                        onClick={handleSubmitPostcardRequest}
                                        disabled={requestStatus === 'sending'}
                                    >
                                        {requestStatus === 'sending' ? '...' : 'Envoyer la demande'}
                                    </button>
                                </div>
                                {requestStatus === 'error' && <p style={{ color: theme.danger, fontFamily: font, fontSize: '11px', marginTop: '8px', fontWeight: 500 }}>Erreur, réessaie.</p>}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;